import { useQuery } from "@tanstack/react-query";
import {
  fetchCanceledOrdersSummary,
  fetchChangesOrdersSummary,
  fetchConfirmedOrdersSummary,
  fetchCustomOrdersSummary,
  fetchDepositRequiredSummary,
  fetchGirabatcomOrdersSummary,
  fetchHoldOrdersSummary,
  fetchIncompleteOrdersSummary,
  fetchNewOrdersSummary,
  fetchPrintedOrdersSummary,
  fetchPrintingHoldOrdersSummary,
  fetchPrintletWorkflowOrdersSummary,
  fetchTeePlusOrdersSummary,
} from "../api/OrderAPIs";
import { fetchReplacementOrdersSummary } from "../api/ReplacementAPIs";
import { fetchReturns } from "../api/ReturnsAPIs";
import { fetchTicketStatusCounts } from "../api/TicketAPIs";
import { canViewOrders, canViewReplacementOrders, canViewReturns, canViewTickets } from "../utils/roles";
import { getOverviewAccess } from "./adminNavConfig";

const countOf = (data) => Number(data?.count || 0);
const amountOf = (data) => Number(data?.totalAmount || 0);
const pick = (data, camel, pascal) => Number(data?.[camel] ?? data?.[pascal] ?? 0);

function ticketCount(counts, key) {
  return Number(
    counts?.[key] ??
      counts?.[key.charAt(0).toUpperCase() + key.slice(1)] ??
      0
  );
}

/**
 * Home tab queries. Keys match the previous dashboard so cached summaries stay warm.
 */
export function useDashboardSummaries(role, permissions = null) {
  const ordersEnabled = canViewOrders(role);
  const ticketsEnabled = canViewTickets(role);
  const replacementsEnabled = canViewReplacementOrders(role);
  const returnsEnabled = canViewReturns(role);
  const common = (enabled) => ({ enabled, staleTime: Infinity });
  const pending = (query, enabled) => enabled && (query.isLoading || query.isPlaceholderData);

  const newOrders = useQuery({
    queryKey: ["newOrdersSummary"],
    queryFn: fetchNewOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const confirmed = useQuery({
    queryKey: ["confirmedOrdersSummary"],
    queryFn: fetchConfirmedOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const canceled = useQuery({
    queryKey: ["canceledOrdersSummary"],
    queryFn: fetchCanceledOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const hold = useQuery({
    queryKey: ["holdOrdersSummary"],
    queryFn: fetchHoldOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const changes = useQuery({
    queryKey: ["changesOrdersSummary"],
    queryFn: fetchChangesOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const custom = useQuery({
    queryKey: ["customOrdersSummary"],
    queryFn: fetchCustomOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const deposit = useQuery({
    queryKey: ["depositRequiredSummary"],
    queryFn: fetchDepositRequiredSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const printed = useQuery({
    queryKey: ["printedOrdersSummary"],
    queryFn: fetchPrintedOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const girabatcom = useQuery({
    queryKey: ["girabatcomOrdersSummary"],
    queryFn: fetchGirabatcomOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const printingHold = useQuery({
    queryKey: ["printingHoldOrdersSummary"],
    queryFn: fetchPrintingHoldOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const incomplete = useQuery({
    queryKey: ["incompleteOrdersSummary"],
    queryFn: fetchIncompleteOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const printlet = useQuery({
    queryKey: ["printletWorkflowOrdersSummary"],
    queryFn: fetchPrintletWorkflowOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0, totalProfit: 0 },
  });
  const teePlus = useQuery({
    queryKey: ["teePlusOrdersSummary"],
    queryFn: fetchTeePlusOrdersSummary,
    ...common(ordersEnabled),
    placeholderData: { count: 0, totalAmount: 0, totalProfit: 0 },
  });
  const tickets = useQuery({
    queryKey: ["ticketStatusCounts"],
    queryFn: fetchTicketStatusCounts,
    ...common(ticketsEnabled),
    placeholderData: { all: 0, open: 0, inProgress: 0, waitingForCustomer: 0, active: 0 },
  });
  const replacements = useQuery({
    queryKey: ["replacementOrdersSummary"],
    queryFn: fetchReplacementOrdersSummary,
    ...common(replacementsEnabled),
    placeholderData: { count: 0, totalCount: 0, totalProductsSold: 0 },
  });
  const returns = useQuery({
    queryKey: ["availableReturnsCount"],
    queryFn: () => fetchReturns({ page: 0, pageSize: 1, status: "Available" }),
    ...common(returnsEnabled),
    placeholderData: { totalCount: 0 },
  });

  const unresolvedTickets =
    ticketCount(tickets.data, "active") ||
    ticketCount(tickets.data, "open") +
      ticketCount(tickets.data, "inProgress") +
      ticketCount(tickets.data, "waitingForCustomer");

  const heroSources = [confirmed, changes, deposit, custom];
  let totalOrders = 0;
  let totalAmount = 0;
  heroSources.forEach((query) => {
    totalOrders += countOf(query.data);
    totalAmount += amountOf(query.data);
  });

  const card = (query) => ({
    count: countOf(query.data),
    amount: amountOf(query.data),
    pieces: Number(query.data?.totalProductsSold || 0),
    loading: pending(query, ordersEnabled),
  });

  return {
    access: getOverviewAccess(role, { permissions }),
    heroLoading: heroSources.some((query) => pending(query, ordersEnabled)),
    totalOrders,
    totalAmount: Math.round(totalAmount),
    cards: {
      newOrders: card(newOrders),
      confirmedOrders: card(confirmed),
      canceledOrders: card(canceled),
      holdOrders: card(hold),
      changesOrders: card(changes),
      customOrders: card(custom),
      depositOrders: card(deposit),
    },
    production: {
      printed: card(printed),
      girabatcom: card(girabatcom),
      printingHold: card(printingHold),
      incomplete: card(incomplete),
      printlet: card(printlet),
      teePlus: card(teePlus),
    },
    tickets: ticketsEnabled
      ? { count: unresolvedTickets, loading: pending(tickets, true) }
      : null,
    replacements: replacementsEnabled
      ? { count: pick(replacements.data, "count", "Count"), loading: pending(replacements, true) }
      : null,
    returns: returnsEnabled
      ? { count: pick(returns.data, "totalCount", "TotalCount"), loading: pending(returns, true) }
      : null,
    badges: {
      newOrders: countOf(newOrders.data),
      confirmedOrders: countOf(confirmed.data),
      canceledOrders: countOf(canceled.data),
      holdOrders: countOf(hold.data),
      changesOrders: countOf(changes.data),
      customOrders: countOf(custom.data),
      depositOrders: countOf(deposit.data),
      tickets: ticketsEnabled ? unresolvedTickets : 0,
    },
  };
}
